/* eslint-disable no-unsafe-optional-chaining */
import * as React from 'react';
import { useRouter } from 'next/router';
import { GetStaticProps } from 'next';
import { useHydrateAtoms } from 'jotai/utils';
import Head from 'next/head';
import { BreadcrumbJsonLd } from 'next-seo';

import { /* createRestaurantsService, createTaxonomyService, */ globalServices } from '@restworld/data-services';
import { Spinner } from '@restworld/ui-ds-base';

import {
  FilterContextValuesType,
  JobExplorerComponentPropType,
} from '../../components/job-explorer/job-explorer.types';
import { JobExplorer } from '../../components/job-explorer';
import { getJobPositionCoordinates, getJobPositionList } from './old';
import { defaultJobListPayload } from '../../components/job-explorer/job-explorer.types';
import {
  domainInterestAtom,
  serverListPayloadAtom,
  serverCoordinatePayloadAtom,
  jobPositionCoordinatesFromServerAtom,
  jobPositionListFromServerAtom,
} from '../../components/job-explorer/job-explorer-server.atoms';
import { jobListParamsAtom, jobLocationPositionListAtom } from '../../components/job-explorer/job-explorer-list.atoms';
import {
  centerPointAtom,
  jobLocationCoordinatesAtom,
  zoomLevelAtom,
} from '../../components/job-explorer/job-explorer-map.atoms';
import { getServerTranslations } from '../../lib/i18n/getServerTranslations';
import { useProcessFilters } from '../../components/job-explorer/job-explorer-more-filter';
import {
  ROLES,
  ALL_ROLE_SLUGS,
  CITIES,
  ALL_CITY_SLUGS,
  SPECIAL_LOCATIONS,
  OCCUPATION_PAGES,
  ALL_OCCUPATION_SLUGS,
  OCCUPATION_CITY_COMBOS,
  buildCategorySeoMeta,
  CATEGORY_INTRO_TEXTS,
} from '../../lib/seo/category-config';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

type IIndexProps = JobExplorerComponentPropType;
export const ExplorerCategoryPages: React.FunctionComponent<IIndexProps> = (props) => {
  const { isFallback, locale, defaultLocale } = useRouter();
  useHydrateAtoms([
    [domainInterestAtom, props.domainInterest],
    [jobLocationPositionListAtom, props.jobPositionListFromServer?.data],
    [jobLocationCoordinatesAtom, props.jobPositionCoordinatesFromServer],
    [serverListPayloadAtom, props.serverListPayload],
    [serverCoordinatePayloadAtom, props.serverCoordinatePayload],
    [jobPositionListFromServerAtom, props.jobPositionListFromServer?.data],
    [jobPositionCoordinatesFromServerAtom, props.jobPositionCoordinatesFromServer],
    [jobListParamsAtom, props.jobListFilterParams || defaultJobListPayload],
    [
      centerPointAtom,
      [
        props.jobListFilterParams?.lon || defaultJobListPayload.lon,
        props.jobListFilterParams?.lat || defaultJobListPayload.lat,
      ],
    ],
    [zoomLevelAtom, props?.jobListFilterParams?.zoom || 10],
  ] as unknown as Parameters<typeof useHydrateAtoms>[0]);

  const { processFilters } = useProcessFilters();

  React.useEffect(() => {
    processFilters(
      {
        domain_interest: props.jobListFilterParams?.domain_interest || undefined,
        is_abroad_job: props.jobListFilterParams?.is_abroad_job || undefined,
        seasonal_job_position: props.jobListFilterParams?.seasonal_job_position ? [true] : undefined,
        ...(props.occupationIdsFilterMap ? { occupation_ids: props.occupationIdsFilterMap } : {}),
      } as unknown as FilterContextValuesType,
      props.jobListFilterParams || defaultJobListPayload
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isFallback)
    return (
      <div className="fixed top-[50%] left-[50%] -mt-[50px] -ml-[50px]">
        <Spinner color="primary" size="md" />
      </div>
    );

  const jobPositions = props.jobPositionListFromServer?.data || [];
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    numberOfItems: jobPositions.length,
    itemListElement: jobPositions
      .filter((jp: any) => jp.semantic_unique_name)
      .map((jp: any, index: number) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://www.restworld.it/posizione/${jp.semantic_unique_name}`,
        name: jp.job_title || jp.occupation?.label_it?.label || '',
      })),
  };

  const introText = props.seoIntroText;

  // The breadcrumb has to name the URL the page indexes as. These categories
  // are self-canonical per locale, so an Italian item on `/en/cerco-lavoro/...`
  // would identify it as a different page than its own canonical does.
  const breadcrumbLocalePrefix = locale && locale !== defaultLocale ? `/${locale}` : '';
  const breadcrumbOrigin = `https://www.restworld.it${breadcrumbLocalePrefix}`;
  const breadcrumbUrl = `${breadcrumbOrigin}${props.canonicalPath || '/cerco-lavoro'}`;

  return (
    <>
      <Head>
        <title>{props.metaTitle}</title>
        <meta name="description" content={props.metaDescription} />
        <meta name="keywords" content={props.keywords} />
        <meta property="og:title" content={props.metaTitle} />
        <meta property="og:description" content={props.metaDescription} />
      </Head>
      <h1 className="sr-only">{props.metaTitle}</h1>
      {introText && <p className="sr-only">{introText}</p>}
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Home',
            item: breadcrumbOrigin,
          },
          {
            position: 2,
            name: props.metaTitle || 'Cerco lavoro',
            item: breadcrumbUrl,
          },
        ]}
      />
      {jobPositions.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd).replace(/<\//g, '<\\/') }}
        />
      )}
      <JobExplorer
        metaDescription={props.metaDescription}
        metaTitle={props.metaTitle}
        canonicalPath={props.canonicalPath}
      />
    </>
  );
};
export default ExplorerCategoryPages;

export const getStaticPaths = async () => {
  const roles = ALL_ROLE_SLUGS;
  const locations = [...ALL_CITY_SLUGS, ...Object.keys(SPECIAL_LOCATIONS)];

  // Role + location combos
  const pathsRoleLocation = roles.map((role) => locations.map((loc) => ({ params: { slug: [role, loc] } }))).flat();
  // Location only
  const pathsOnlyLocation = locations.map((loc) => ({ params: { slug: [loc] } }));
  // Role only
  const pathsOnlyRole = roles.map((role) => ({ params: { slug: [role] } }));

  // Occupation pages
  const pathsOccupation = ALL_OCCUPATION_SLUGS.map((occ) => ({ params: { slug: [occ] } }));
  // Occupation + city combos
  const pathsOccupationCity = ALL_OCCUPATION_SLUGS.map((occ) =>
    OCCUPATION_CITY_COMBOS.map((city) => ({ params: { slug: [occ, city] } }))
  ).flat();

  // Stagionale con alloggio
  const pathsSpecial = [{ params: { slug: ['stagionale', 'con-alloggio'] } }];

  return {
    paths: [
      ...pathsRoleLocation,
      ...pathsOnlyLocation,
      ...pathsOnlyRole,
      ...pathsOccupation,
      ...pathsOccupationCity,
      ...pathsSpecial,
    ],
    // fallback: 'blocking' — pages not in the paths list are generated on-demand.
    // This also makes SSG resilient to transient API failures at build time.
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<IIndexProps> = async ({ locale, params }) => {
  const slugArr = (params?.slug as string[]) || [];

  // --- Parse slug segments ---
  let inputDomainInterest = '';
  let inputLocation = '';
  let occupationSlug = '';
  let isAccommodation = false;

  if (slugArr.length === 2 && slugArr[0] === 'stagionale' && slugArr[1] === 'con-alloggio') {
    // /cerco-lavoro/stagionale/con-alloggio
    inputLocation = 'stagionale';
    isAccommodation = true;
  } else if (slugArr.length === 2) {
    const first = slugArr[0];
    const second = slugArr[1];

    if (ALL_OCCUPATION_SLUGS.includes(first)) {
      // /cerco-lavoro/{occupation}/{city}
      occupationSlug = first;
      inputLocation = second;
    } else if (ALL_ROLE_SLUGS.includes(first)) {
      // /cerco-lavoro/{role}/{location}
      inputDomainInterest = first;
      inputLocation = second;
    } else {
      // Fallback: treat as role/location
      inputDomainInterest = first;
      inputLocation = second;
    }
  } else if (slugArr.length === 1) {
    const segment = slugArr[0];

    if (ALL_OCCUPATION_SLUGS.includes(segment)) {
      occupationSlug = segment;
    } else if (ALL_ROLE_SLUGS.includes(segment)) {
      inputDomainInterest = segment;
    } else {
      inputLocation = segment;
    }
  }

  let serverProps = {
    props: {
      domainInterest: null,
      jobPositionListFromServer: null,
      jobPositionCoordinatesFromServer: null,
    },
  };
  const transConfig = await getServerTranslations(locale, [
    'common',
    'job-explorer-page',
    'auth-flow',
    'onboarding-page',
  ]);

  let domainInterests = null;
  let domainInterestID = undefined;
  try {
    domainInterests = (await globalServices({ baseUrl }).fetchVariableOptions('domain_interest'))?.data;
    // Resolve domain interest ID from role slug — check ROLES map first, then fallback to direct key
    const domainKey = ROLES[inputDomainInterest]?.domainKey || inputDomainInterest;
    domainInterestID = domainInterests?.find((di) => di.key === domainKey)?.id;
  } catch (error) {
    console.error('Error fetching domain interests:', error);
  }

  let lat = defaultJobListPayload.lat;
  let lon = defaultJobListPayload.lon;
  let seasonal_job_position = false;
  let is_abroad_job = false;
  let radius = defaultJobListPayload.radius;
  let zoom = 3.5;

  // Resolve city coordinates
  const cityConfig = CITIES[inputLocation];
  if (cityConfig) {
    lat = cityConfig.lat;
    lon = cityConfig.lon;
    radius = cityConfig.radius;
    zoom = cityConfig.zoom;
  } else if (inputLocation === 'estero') {
    is_abroad_job = true;
    radius = defaultJobListPayload.radius;
    zoom = 2;
  } else if (inputLocation === 'stagionale') {
    seasonal_job_position = true;
    radius = defaultJobListPayload.radius;
    zoom = 4;
  }

  // Build API payload
  const occupationConfig = OCCUPATION_PAGES[occupationSlug];

  // For occupation pages, resolve domain_interest IDs from the occupation's domainKeys
  let occupationDomainInterestIds: string[] = [];
  const occupationIdsFilterMap: Record<string, string[]> = {};
  if (occupationConfig) {
    occupationDomainInterestIds = occupationConfig.domainKeys
      .map((key) => domainInterests?.find((di) => di.key === key)?.id)
      .filter(Boolean) as string[];
    // Build occupation_ids in the format the client-side filter system expects: { domainId: [occIds] }
    for (const diId of occupationDomainInterestIds) {
      occupationIdsFilterMap[diId] = occupationConfig.occupationIds;
    }
  }

  const payload: any = {
    ...defaultJobListPayload,
    limit: 100,
    domain_interest: occupationConfig
      ? occupationDomainInterestIds
      : inputDomainInterest && domainInterestID
      ? [domainInterestID]
      : [],
    lat,
    lon,
    radius,
    seasonal_job_position: isAccommodation ? true : seasonal_job_position,
    is_abroad_job,
    zoom,
  };

  if (occupationConfig) {
    payload.occupation_ids = occupationConfig.occupationIds;
  }

  // Determine intro text key — prefer specific combo key, fallback to role or location
  let introKey = '';
  if (isAccommodation) {
    introKey = 'stagionale/con-alloggio';
  } else if (occupationSlug) {
    introKey = occupationSlug;
  } else if (inputDomainInterest && inputLocation && SPECIAL_LOCATIONS[inputLocation]) {
    introKey = `${inputDomainInterest}/${inputLocation}`;
  } else if (inputDomainInterest) {
    introKey = inputDomainInterest;
  } else if (inputLocation && SPECIAL_LOCATIONS[inputLocation]) {
    introKey = inputLocation;
  }

  const seoIntroText = CATEGORY_INTRO_TEXTS[introKey] || '';

  try {
    const [jobPositionListFromServer, jobPositionCoordinatesFromServer] = await Promise.all([
      getJobPositionList({ payload }),
      getJobPositionCoordinates({}),
    ]);

    // Update meta title with actual count
    const actualCount = jobPositionListFromServer?.data?.length || 0;
    const seoMetaWithCount = buildCategorySeoMeta({
      role: inputDomainInterest || undefined,
      location: inputLocation || undefined,
      occupationSlug: occupationSlug || undefined,
      isAbroad: is_abroad_job,
      isSeasonal: seasonal_job_position,
      isAccommodation,
      count: actualCount,
    });

    const props = {
      domainInterest: domainInterests,
      jobPositionListFromServer,
      jobPositionCoordinatesFromServer,
      jobListFilterParams: payload,
      metaTitle: seoMetaWithCount.metaTitle,
      metaDescription: seoMetaWithCount.metaDescription,
      canonicalPath: seoMetaWithCount.canonicalPath,
      keywords: seoMetaWithCount.keywords,
      seoIntroText,
      ...(Object.keys(occupationIdsFilterMap).length > 0 ? { occupationIdsFilterMap } : {}),
      ...transConfig,
    };
    serverProps = {
      props,
    };
  } catch (error) {
    // Error handled silently — fallback to default serverProps
  }
  return {
    ...serverProps,
    revalidate: 60,
  };
};
